"use client";

import { SEOContentForProjectOrSup } from "@/components/sup/seo/content-project-sup";
import { useTotalCountFetch } from "@/hooks/use-total-count-fetch";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { useSearchPageMapData } from "@/providers/search";
import { memo, useMemo } from "react";

/**
 * Wrapper for SeoBanner on search pages
 * Provides search-specific data from filters and map context
 */
export const SEOContentForSearch = memo(function SEOContentForSearch() {
	const { filters } = useUrlFilters();

	// Remove bounds and map UI state from filters for total count query
	const filtersWithoutBounds = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { bounds: _bounds, coordinates: _coordinates, zoom: _zoom, ...rest } = filters;

		// Remove empty arrays, undefined values, and map UI state
		const cleanedFilters: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(rest)) {
			// Skip empty arrays
			if (Array.isArray(value) && value.length === 0) continue;
			// Skip undefined values
			if (value === undefined) continue;
			// Include everything else (actual search filters like propertyType, bedrooms, etc.)
			cleanedFilters[key] = value;
		}

		return cleanedFilters as Partial<typeof rest>;
	}, [filters]);

	// Fetch total count when filters change (network-only, bypasses all caching)
	useTotalCountFetch({ filters: filtersWithoutBounds, enabled: true });

	// Get total count from the map store (counts all properties without bounds)
	const totalCountWithoutBounds = useSearchPageMapData(state => state.totalCountWithoutBounds);

	// Get bedroom range if set
	const bedrooms = filters.bedrooms?.min;

	// Extract locality and project from locations
	const locality = filters.locations?.find(loc => loc.type === "landmark")?.name;
	const project = filters.locations?.find(loc => loc.type === "project")?.name;

	return (
		<SEOContentForProjectOrSup
			availableUnitsCount={totalCountWithoutBounds ?? undefined}
			availableBedrooms={bedrooms ? [bedrooms] : undefined}
			locality={locality}
			projectName={project}
			propertyTypes={filters.propertyType ? [filters.propertyType] : undefined}
		/>
	);
});
